
import React, { useState } from 'react';
import {
  ChevronRight, ChevronLeft, CheckCircle, Sparkles, Briefcase, Users, FileText,
  Download, Copy, Loader, Upload, X, TrendingUp, DollarSign, Target,
  ArrowRight, Zap, Clock, AlertCircle, Database, BarChart2, Shield
} from 'lucide-react';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://careergpt-backend-884986469457.us-central1.run.app';
const BRAND_MARK_SRC = '/cernuvo-brand-mark.png';

function BrandLogo({ variant = 'wordmark', className = '', textClassName = '' }) {
  if (variant === 'icon') {
    return (
      <img
        src={BRAND_MARK_SRC}
        alt="Cernuvo"
        className={`object-contain ${className}`}
      />
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img
        src={BRAND_MARK_SRC}
        alt="Cernuvo brand mark"
        className="h-12 w-12 object-contain"
      />
      <span className={`text-3xl font-bold text-gray-900 tracking-tight ${textClassName}`}>
        Cernuvo
      </span>
    </div>
  );
}

// Friendly source name mapping
const SOURCE_LABELS = {
  'backpocket_specs.docx': { label: 'Industry Research', color: 'bg-emerald-100 text-emerald-700' },
  'healthcare_benchmarks.txt': { label: 'Healthcare Benchmarks', color: 'bg-blue-100 text-blue-700' },
  'onet_healthcare_roles.txt': { label: 'O*NET Roles', color: 'bg-purple-100 text-purple-700' },
  'health_software_proscons.txt': { label: 'Software Guide', color: 'bg-orange-100 text-orange-700' },
  'specs.docx': { label: 'BackPocket Specs', color: 'bg-emerald-100 text-emerald-700' },
};

const DATA_SOURCES = ['MGMA', 'BLS/O*NET', 'CMS', 'SHRM', 'Phreesia', 'AMA', 'AAPC', 'HFMA'];

const demoScenarios = {
  scenario1: {
    name: "Front Desk",
    data: {
      practice_type: "Primary Care", practice_size: "2-5 providers", region: "Northeast US",
      current_role_title: "Front Desk Representative",
      current_job_description: `• Inbound scheduling calls - 30%\n• Patient check-in and registration - 20%\n• Insurance verification - 20%\n• Appointment reminders - 15%\n• Patient inquiries - 10%\n• Co-pay collection - 5%`,
      current_software: "Epic, phone system", ai_tools_planned: "Epic AI Scheduling, Phreesia AI",
      pain_points: ["High call volume", "Manual insurance verification"], additional_notes: "80-100 calls per day"
    }
  },
  scenario2: {
    name: "Medical Assistant",
    data: {
      practice_type: "Solo Practice", practice_size: "1 provider", region: "West US",
      current_role_title: "Medical Assistant",
      current_job_description: `• Clinical documentation - 25%\n• Patient rooming, vitals - 20%\n• Scheduling management - 15%\n• Prior authorization - 15%\n• Patient communication - 10%\n• Inventory management - 8%\n• Procedure room prep - 7%`,
      current_software: "eClinicalWorks EHR", ai_tools_planned: "Nuance DAX, Suki AI",
      pain_points: ["Documentation burden", "Prior auth delays"], additional_notes: "Chronic disease focus"
    }
  },
  scenario3: {
    name: "Billing Specialist",
    data: {
      practice_type: "Multi-Specialty", practice_size: "11-20 providers", region: "Southeast US",
      current_role_title: "Billing Specialist",
      current_job_description: `• Medical coding (CPT/ICD-10) - 25%\n• Claim submission - 20%\n• Prior authorization - 20%\n• Denial management - 15%\n• Payment posting - 10%\n• Patient billing - 10%`,
      current_software: "Cerner EHR, Kareo billing", ai_tools_planned: "Waystar, Olive AI",
      pain_points: ["High denial rates", "Manual coding errors"], additional_notes: "8-12% denial rate"
    }
  }
};

const commonPainPoints = {
  "front desk": ["High call volume", "Long hold times", "Manual insurance verification", "Repetitive reminder calls", "Scheduling conflicts", "No-shows"],
  "medical assistant": ["Documentation burden", "After-hours charting", "Prior authorization delays", "Supply management", "Patient follow-up backlog"],
  "billing": ["High denial rates", "Manual coding errors", "Prior auth delays", "Payer rule changes", "Payment posting backlog"],
  "default": ["High workload", "Repetitive manual tasks", "Documentation requirements", "Communication delays", "Training gaps"]
};

export default function CernuvoApp() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    practice_type: '', practice_size: '', region: '',
    current_role_title: '', current_job_description: '',
    current_software: '', ai_tools_planned: '',
    pain_points: [], additional_notes: ''
  });
  const [outputData, setOutputData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [apiError, setApiError] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [suggestedPainPoints, setSuggestedPainPoints] = useState([]);
  const [customPainPoint, setCustomPainPoint] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');

  const resetForm = () => {
    setCurrentStep(1); setOutputData(null);
    setFormData({ practice_type: '', practice_size: '', region: '', current_role_title: '', current_job_description: '', current_software: '', ai_tools_planned: '', pain_points: [], additional_notes: '' });
    setUploadedFileName(''); setSuggestedPainPoints([]); setApiError(null);
  };

  const loadDemoScenario = (key) => {
    const scenario = demoScenarios[key];
    if (scenario) {
      setFormData(scenario.data); setCurrentStep(1); setOutputData(null);
      setApiError(null); setUploadedFileName('');
      generatePainPointSuggestions(scenario.data.current_role_title);
    }
  };

  const generatePainPointSuggestions = (roleTitle) => {
    const r = roleTitle.toLowerCase();
    let s = commonPainPoints.default;
    if (r.includes('front desk') || r.includes('receptionist')) s = commonPainPoints["front desk"];
    else if (r.includes('medical assistant') || r.includes('ma')) s = commonPainPoints["medical assistant"];
    else if (r.includes('billing') || r.includes('coder')) s = commonPainPoints["billing"];
    setSuggestedPainPoints(s);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsExtracting(true); setApiError(null); setUploadedFileName('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/upload-job-description`, { method: 'POST', body: fd });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Upload failed');
      if (result.extracted_tasks?.length > 0) {
        updateFormData('current_job_description', result.extracted_tasks.map(t => `• ${t.task} - ${t.percentage}%`).join('\n'));
        if (result.job_title_detected && result.job_title_detected !== 'Unknown') {
          updateFormData('current_role_title', result.job_title_detected);
          generatePainPointSuggestions(result.job_title_detected);
        }
        setUploadedFileName(file.name);
      } else throw new Error('No tasks found in the file.');
    } catch (err) {
      setApiError(`Upload failed: ${err.message}`); setUploadedFileName('');
    } finally { setIsExtracting(false); event.target.value = ''; }
  };

  const togglePainPoint = (p) => {
    const cur = formData.pain_points || [];
    updateFormData('pain_points', cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p]);
  };

  const addCustomPainPoint = () => {
    if (customPainPoint.trim()) {
      updateFormData('pain_points', [...(formData.pain_points || []), customPainPoint.trim()]);
      setCustomPainPoint('');
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'current_role_title' && value.length > 3) generatePainPointSuggestions(value);
  };

  const nextStep = () => { if (currentStep < 2) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true); setApiError(null);
    const msgs = ['Analyzing current role...', 'Searching benchmark data...', 'Calculating ROI with MGMA data...', 'Building skill transition map...', 'Generating transformation plan...'];
    let i = 0;
    setLoadingMessage(msgs[0]);
    const iv = setInterval(() => { i = (i + 1) % msgs.length; setLoadingMessage(msgs[i]); }, 2000);
    try {
      const submitData = {
        practice_type: formData.practice_type || '', practice_size: formData.practice_size || '',
        region: formData.region || '', current_role_title: formData.current_role_title || '',
        current_job_description: formData.current_job_description || '',
        current_software: formData.current_software || '', ai_tools_planned: formData.ai_tools_planned || '',
        pain_points: Array.isArray(formData.pain_points) ? formData.pain_points.join(', ') : (formData.pain_points || ''),
        additional_notes: formData.additional_notes || ''
      };
      const res = await fetch(`${API_BASE_URL}/generate-role`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(submitData)
      });
      if (!res.ok) throw new Error(`Error: ${res.status} - ${await res.text()}`);
      const result = await res.json();
      setOutputData(result.data || result); setCurrentStep(3);
    } catch (err) { setApiError(err.message); }
    finally { clearInterval(iv); setIsLoading(false); }
  };

 const handleDownload = async () => {
  if (!outputData) return;
  try {
    // Merge user's original form inputs with the LLM output
    // so the downloaded document shows both Current and Evolved sides
    const exportPayload = {
      ...outputData,
      current_role_title: outputData.current_role_title || formData.current_role_title,
      current_job_description: outputData.current_job_description || formData.current_job_description,
      practice_type: outputData.practice_type || formData.practice_type,
      practice_size: outputData.practice_size || formData.practice_size,
      region: outputData.region || formData.region,
    };
    const res = await fetch(`${API_BASE_URL}/export/docx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(exportPayload)
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${outputData.new_role_title || 'transformation-plan'}.docx`; a.click();
  } catch (err) { alert('Download failed: ' + err.message); }
};
  const handleCopy = () => {
    if (!outputData) return;
    navigator.clipboard.writeText(JSON.stringify(outputData, null, 2));
    alert('Copied to clipboard!');
  };

  if (isLoading) return <LoadingState message={loadingMessage} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex flex-col items-center justify-center mb-4">
            <div className="flex items-center gap-3 mb-1">
              <BrandLogo />
            </div>
            {/* Data sources banner */}
            <div className="flex items-center gap-2 mt-3 flex-wrap justify-center">
              <span className="text-xs text-gray-400 flex items-center gap-1"><Database size={11} /> Powered by:</span>
              {DATA_SOURCES.map(s => (
                <span key={s} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full border border-gray-200">{s}</span>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-3 flex-wrap">
            {Object.entries(demoScenarios).map(([key, scenario]) => (
              <button key={key} onClick={() => loadDemoScenario(key)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors border border-gray-200">
                📋 {scenario.name}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {apiError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{apiError}</p>
            </div>
            <button onClick={() => setApiError(null)} className="text-red-400 hover:text-red-600"><X size={18} /></button>
          </div>
        )}

        <ProgressBar currentStep={currentStep} />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
          {currentStep === 1 && (
            <Step1CurrentRole formData={formData} updateFormData={updateFormData}
              onFileUpload={handleFileUpload} isExtracting={isExtracting}
              uploadedFileName={uploadedFileName} suggestedPainPoints={suggestedPainPoints}
              togglePainPoint={togglePainPoint} customPainPoint={customPainPoint}
              setCustomPainPoint={setCustomPainPoint} addCustomPainPoint={addCustomPainPoint} />
          )}
          {currentStep === 2 && <Step2Software formData={formData} updateFormData={updateFormData} />}
          {currentStep === 3 && <Step3Output data={outputData} formData={formData} onDownload={handleDownload} onCopy={handleCopy} />}
        </div>

        <div className="flex justify-between mt-6">
          <button onClick={prevStep} disabled={currentStep === 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}>
            <ChevronLeft size={20} /> Previous
          </button>
          {currentStep === 1 ? (
            <button onClick={nextStep} className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all shadow-sm">
              Next <ChevronRight size={20} />
            </button>
          ) : currentStep === 2 ? (
            <button onClick={handleSubmit} className="flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all shadow-sm">
              <Sparkles size={18} /> Generate Plan
            </button>
          ) : (
            <button onClick={resetForm} className="px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all">
              Start New Analysis
            </button>
          )}
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between text-sm text-gray-500 flex-wrap gap-2">
            <p>© 2026 BackPocket Talent. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Shield size={12} /> HIPAA-aware outputs</span>
              <span className="flex items-center gap-1"><BarChart2 size={12} /> Backed by 8 industry data sources</span>
              <p>AI-Powered Workforce Transformation</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ProgressBar({ currentStep }) {
  const steps = [
    { num: 1, label: 'Current Role', icon: Users },
    { num: 2, label: 'Software & Tools', icon: Briefcase },
    { num: 3, label: 'Transformation Plan', icon: FileText }
  ];
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const done = currentStep > step.num, cur = currentStep === step.num;
          return (
            <React.Fragment key={step.num}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${done ? 'bg-emerald-600 border-emerald-600 text-white' : cur ? 'bg-white border-emerald-600 text-emerald-600' : 'bg-white border-gray-300 text-gray-400'}`}>
                  {done ? <CheckCircle size={24} /> : <Icon size={22} />}
                </div>
                <span className={`mt-2 text-sm font-medium ${cur ? 'text-emerald-600' : done ? 'text-gray-900' : 'text-gray-400'}`}>{step.label}</span>
              </div>
              {idx < steps.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${currentStep > step.num ? 'bg-emerald-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function LoadingState({ message }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-sm">
        <div className="relative w-20 h-20 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-emerald-100 border-t-emerald-600 animate-spin"></div>
          <BrandLogo variant="icon" className="absolute inset-2 w-auto h-auto max-w-full max-h-full p-1" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating Your Plan</h2>
        <p className="text-gray-600 animate-pulse">{message}</p>
        <p className="text-sm text-gray-400 mt-4">Searching MGMA, BLS, and BackPocket data sources...</p>
        <div className="flex justify-center gap-2 mt-4 flex-wrap">
          {['MGMA', 'BLS', 'CMS', 'SHRM'].map(s => (
            <span key={s} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200 animate-pulse">{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step1CurrentRole({ formData, updateFormData, onFileUpload, isExtracting, uploadedFileName, suggestedPainPoints, togglePainPoint, customPainPoint, setCustomPainPoint, addCustomPainPoint }) {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-gray-900">Current Role Information</h2>
        <p className="text-gray-500 text-sm mt-1">Enter details about the current role you want to transform</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Practice Type <span className="text-red-500">*</span></label>
          <input type="text" value={formData.practice_type} onChange={e => updateFormData('practice_type', e.target.value)}
            placeholder="e.g., Primary Care" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Practice Size <span className="text-red-500">*</span></label>
          <select value={formData.practice_size} onChange={e => updateFormData('practice_size', e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white transition-all">
            <option value="">Select size...</option>
            <option value="1 provider">Solo (1 provider)</option>
            <option value="2-5 providers">Small (2-5 providers)</option>
            <option value="6-10 providers">Medium (6-10 providers)</option>
            <option value="11-20 providers">Large (11-20 providers)</option>
            <option value="20+ providers">Enterprise (20+ providers)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Region <span className="text-red-500">*</span></label>
          <input type="text" value={formData.region} onChange={e => updateFormData('region', e.target.value)}
            placeholder="e.g., Northeast US" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Role Title <span className="text-red-500">*</span></label>
        <input type="text" value={formData.current_role_title} onChange={e => updateFormData('current_role_title', e.target.value)}
          placeholder="e.g., Front Desk Representative" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Job Description <span className="text-red-500">*</span></label>
        <p className="text-xs text-gray-500 mb-2">Upload a file or enter tasks manually (should total 100%)</p>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <label className="px-4 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 cursor-pointer flex items-center gap-2 transition-all text-sm">
            <Upload size={16} />
            {isExtracting ? 'Processing...' : 'Upload Job Description'}
            <input type="file" accept=".pdf,.docx,.txt" onChange={onFileUpload} className="hidden" disabled={isExtracting} />
          </label>
          {isExtracting && <Loader className="w-5 h-5 text-emerald-600 animate-spin" />}
          {uploadedFileName && !isExtracting && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle size={16} className="text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">{uploadedFileName}</span>
            </div>
          )}
        </div>
        <textarea value={formData.current_job_description} onChange={e => updateFormData('current_job_description', e.target.value)}
          placeholder={"• Inbound scheduling calls - 30%\n• Patient check-in - 20%\n• Insurance verification - 20%\n• Appointment reminders - 15%\n• Patient inquiries - 10%\n• Co-pay collection - 5%"}
          rows={7} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm transition-all" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Pain Points</label>
        <p className="text-xs text-gray-500 mb-2">Select common issues or add your own</p>
        {suggestedPainPoints.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {suggestedPainPoints.map((point, idx) => {
              const sel = (formData.pain_points || []).includes(point);
              return (
                <button key={idx} onClick={() => togglePainPoint(point)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${sel ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {sel && '✓ '}{point}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex gap-2">
          <input type="text" value={customPainPoint} onChange={e => setCustomPainPoint(e.target.value)}
            placeholder="Add custom pain point..." className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
            onKeyPress={e => e.key === 'Enter' && addCustomPainPoint()} />
          <button onClick={addCustomPainPoint} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">Add</button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
        <textarea value={formData.additional_notes} onChange={e => updateFormData('additional_notes', e.target.value)}
          placeholder="e.g., Patient demographics, daily volume..." rows={2}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm transition-all" />
      </div>
    </div>
  );
}

function Step2Software({ formData, updateFormData }) {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-gray-900">Software & AI Tools</h2>
        <p className="text-gray-500 text-sm mt-1">List current systems and planned AI implementations</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Software & Systems <span className="text-red-500">*</span></label>
        <textarea value={formData.current_software} onChange={e => updateFormData('current_software', e.target.value)}
          placeholder="e.g., Epic EHR, Microsoft Office, phone system..." rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition-all" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">AI Tools Planned <span className="text-red-500">*</span></label>
        <textarea value={formData.ai_tools_planned} onChange={e => updateFormData('ai_tools_planned', e.target.value)}
          placeholder="e.g., Epic AI Scheduling, Nuance DAX, Luma Health..." rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition-all" />
      </div>
    </div>
  );
}

function Step3Output({ data, formData, onDownload, onCopy }) {
  if (!data) return <div className="text-center py-16"><Loader className="w-10 h-10 text-emerald-600 animate-spin mx-auto" /></div>;

  const roiData = data.roi_summary || {};
  const skillTransition = data.skill_transition_map || [];
  const citations = data.citations || [];
  const usedSources = [...new Set(citations.map(c => c.source_name).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Workforce Transformation Plan</h2>
          <p className="text-gray-500 text-sm mt-1">AI-powered role evolution analysis</p>
          {/* Data source badges */}
          {usedSources.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {usedSources.map(src => {
                const meta = SOURCE_LABELS[src] || { label: src, color: 'bg-gray-100 text-gray-600' };
                return <span key={src} className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>{meta.label}</span>;
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={onDownload} className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 flex items-center gap-2 text-sm transition-all">
            <Download size={16} /> Download
          </button>
          <button onClick={onCopy} className="px-4 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 flex items-center gap-2 text-sm transition-all">
            <Copy size={16} /> Copy
          </button>
        </div>
      </div>

      {/* Role Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <Users size={16} className="text-gray-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">Current Role</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{formData.current_role_title}</h3>
          <p className="text-sm text-gray-600 mt-2">{formData.practice_type} • {formData.practice_size}</p>
          <p className="text-xs text-gray-400 mt-1">{formData.region}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-5 border-2 border-emerald-500">
          <div className="flex items-center gap-2 mb-3">
            <BrandLogo variant="icon" className="w-8 h-8" />
            <span className="text-sm font-medium text-emerald-600">Evolved Role</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900">{data.new_role_title || 'AI-Enhanced ' + formData.current_role_title}</h3>
          <p className="text-sm text-gray-700 mt-2">{data.role_transformation_summary}</p>
        </div>
      </div>
      {data.recommended_ai_tools?.length > 0 && (
  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
    <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
      <Sparkles size={18} className="text-emerald-600" /> Recommended Additions to Your Stack
    </h3>
    <p className="text-xs text-gray-500 mb-4">
      Tools that fill gaps your current selections don't cover
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {data.recommended_ai_tools.map((tool, idx) => {
        // Normalize: handle both new object format and legacy string format
        const isObject = typeof tool === 'object' && tool !== null;
        const name = isObject ? tool.name : tool;
        const category = isObject ? tool.category : null;
        const description = isObject ? tool.description : null;
 
        return (
          <div
            key={idx}
            className="bg-white p-4 rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all flex flex-col"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-semibold text-gray-900">{name}</div>
              {category && (
                <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200 font-medium flex-shrink-0 whitespace-nowrap">
                  {category}
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm text-gray-600 leading-relaxed mt-1">
                {description}
              </p>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}

      {/* ROI Section */}
      {Object.keys(roiData).length > 0 && (
        <div className="bg-gray-900 rounded-lg p-5 text-white">
          <h3 className="font-bold mb-1 flex items-center gap-2">
            <DollarSign size={20} /> Return on Investment
          </h3>
          <p className="text-xs text-gray-400 mb-4">Calculated using MGMA, BLS, and Phreesia benchmark data</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: Clock, label: 'Time Saved Per Week', val: roiData.time_saved_per_week },
              { icon: DollarSign, label: 'Cost Impact', val: roiData.cost_impact },
              { icon: TrendingUp, label: 'Revenue Impact', val: roiData.revenue_impact },
              { icon: Target, label: 'Payback Period', val: roiData.payback_period },
            ].map(({ icon: Icon, label, val }) => (
              <div key={label} className="bg-white/10 rounded-lg p-4">
                <Icon size={24} className="mb-2 text-emerald-400" />
                <div className="text-sm text-gray-300 mb-1">{label}</div>
                <div className="text-lg font-bold">{val || 'N/A'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ROI Proof Section */}
      {data.roi_breakdown && (
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
            <BarChart2 size={18} className="text-emerald-600" /> How We Calculate Your ROI
          </h3>
          <p className="text-xs text-gray-400 mb-4">Every number is backed by a real data source and a transparent formula</p>
          <div className="space-y-3">
            {[
              { key: 'time_saved', label: 'Time Saved Per Week', icon: Clock, color: 'bg-blue-50 border-blue-200' },
              { key: 'cost_impact', label: 'Cost Impact', icon: DollarSign, color: 'bg-green-50 border-green-200' },
              { key: 'revenue_impact', label: 'Revenue Impact', icon: TrendingUp, color: 'bg-purple-50 border-purple-200' },
              { key: 'payback_period', label: 'Payback Period', icon: Target, color: 'bg-orange-50 border-orange-200' },
            ].map(({ key, label, icon: Icon, color }) => {
              const item = data.roi_breakdown[key];
              if (!item) return null;
              return (
                <div key={key} className={`rounded-lg p-4 border ${color}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className="text-gray-600" />
                    <span className="text-sm font-semibold text-gray-800">{label}</span>
                    <span className="ml-auto text-lg font-bold text-gray-900">{item.value}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="font-medium text-gray-500 flex-shrink-0">Formula:</span>
                      <span>{item.formula}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="font-medium text-gray-500 flex-shrink-0">Source:</span>
                      <span className="text-emerald-700 font-medium">{item.source}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Human Value */}
      <div className="bg-emerald-600 rounded-lg p-5 text-white">
        <h3 className="font-bold mb-2 flex items-center gap-2">💎 Why This Role Matters More After AI</h3>
        <p className="leading-relaxed text-emerald-50">{data.human_value_statement || 'Human judgment, empathy, and relationship-building remain essential.'}</p>
      </div>

      {/* Skill Transition Map */}
      {skillTransition.length > 0 && (
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target size={18} className="text-emerald-600" /> 6-Month Skill Transition Map
          </h3>
          <div className="space-y-3">
            {skillTransition.map((item, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-emerald-600 text-white text-xs px-2 py-1 rounded-full font-medium">{item.phase}</span>
                  <span className="text-sm font-semibold text-gray-800">{item.focus}</span>
                </div>
                <div className="text-sm text-gray-600 flex items-start gap-2">
                  <ArrowRight size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  {item.action}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Zap size={16} className="text-red-500" /> Tasks Being Automated
          </h4>
          {data.automated_tasks?.length > 0 ? (
            <ul className="space-y-2">
              {data.automated_tasks.map((task, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-5 h-5 bg-red-100 text-red-500 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{idx + 1}</span>
                  {task}
                </li>
              ))}
            </ul>
          ) : <p className="text-gray-400 text-sm italic">No tasks specified</p>}
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" /> New Daily Duties
          </h4>
          {data.evolved_daily_duties?.length > 0 ? (
            <ul className="space-y-2">
              {data.evolved_daily_duties.map((duty, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">{idx + 1}</span>
                  {duty}
                </li>
              ))}
            </ul>
          ) : <p className="text-gray-400 text-sm italic">No duties specified</p>}
        </div>
      </div>

      {/* Skills & Software */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3">💻 Required Software</h4>
          {data.required_software?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.required_software.map((s, idx) => (
                <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm border border-gray-200">{s}</span>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm italic">No software specified</p>}
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3">🎯 New Skills Needed</h4>
          {data.new_skills_needed?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.new_skills_needed.map((s, idx) => (
                <span key={idx} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm border border-emerald-200">{s}</span>
              ))}
            </div>
          ) : <p className="text-gray-400 text-sm italic">No skills specified</p>}
        </div>
      </div>

      {/* Recommended AI Tools */}

 

      {/* Practice Context */}
      {data.practice_context_notes && (
        <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
          <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            🏥 Practice Context
          </h3>
          <p className="text-sm text-blue-800 leading-relaxed">{data.practice_context_notes}</p>
        </div>
      )}

      {/* Citations */}
      {citations.length > 0 && (
        <div className="bg-white rounded-lg p-5 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">📚 Sources & Citations</h3>
          <p className="text-xs text-gray-400 mb-4">Results grounded in BackPocket specs, O*NET role data, and healthcare benchmarks</p>
          <div className="space-y-2">
            {citations.map((citation, idx) => {
              const meta = SOURCE_LABELS[citation.source_name] || { label: citation.source_name, color: 'bg-gray-100 text-gray-600' };
              return (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium flex-shrink-0">{citation.id || idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>{meta.label}</span>
                    </div>
                    {citation.excerpt && <p className="text-gray-600 text-xs line-clamp-2">{citation.excerpt}</p>}
                    {citation.url && (
                      <a href={citation.url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline text-xs mt-1 inline-block">View Source →</a>
                    )}
                  </div>
                  {citation.relevance_score && (
                    <div className="flex-shrink-0 text-right">
                      <div className="text-xs font-bold text-emerald-600">{(citation.relevance_score * 100).toFixed(0)}%</div>
                      <div className="text-xs text-gray-400">match</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}